import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { Typography, Box } from '@mui/material';
import { Construction } from '@mui/icons-material';

interface MaintenanceScreenProps {
    storeName?: string;
    primaryColor?: string;
}

export const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ 
    storeName = 'This Store',
    primaryColor = '#3b82f6'
}) => {
    const [fadeAnim] = useState(new Animated.Value(0));
    const [floatAnim] = useState(new Animated.Value(0));

    // Darken color for background (make it 70% darker)
    const darkenColor = (color: string, percent: number) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = Math.max(0, (num >> 16) - amt);
        const G = Math.max(0, (num >> 8 & 0x00FF) - amt);
        const B = Math.max(0, (num & 0x0000FF) - amt);
        return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
    };

    const darkBg = darkenColor(primaryColor, 75);
    const mediumBg = darkenColor(primaryColor, 95);

    useEffect(() => {
        // Fade in animation
        Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: false,
        }).start();

        // Floating animation for icon
        Animated.loop(
            Animated.sequence([
                Animated.timing(floatAnim, {
                    toValue: 1,
                    duration: 2000,
                    useNativeDriver: false,
                }),
                Animated.timing(floatAnim, {
                    toValue: 0,
                    duration: 2000,
                    useNativeDriver: false,
                })
            ])
        ).start();
    }, []);

    const iconFloat = floatAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -15],
    });

    return (
        <View style={[styles.container, { backgroundColor: darkBg }]}>
            {/* Background with enhanced grid pattern */}
            <Box sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: mediumBg,
            }}>
                {/* Primary grid - large pattern with offset */}
                <Box sx={{ 
                    position: 'absolute',
                    top: '-10%',
                    left: '-5%',
                    right: '-5%',
                    bottom: '-10%',
                    backgroundImage: `
                        linear-gradient(${primaryColor}18 2px, transparent 2px),
                        linear-gradient(90deg, ${primaryColor}18 2px, transparent 2px)
                    `,
                    backgroundSize: '100px 100px',
                    backgroundPosition: '0 -100px',
                    opacity: 0.5,
                    transform: 'rotate(-2deg)',
                }} />
                
                {/* Secondary grid - smaller, diagonal offset */}
                <Box sx={{ 
                    position: 'absolute',
                    top: '-10%',
                    left: '-5%',
                    right: '-5%',
                    bottom: '-10%',
                    backgroundImage: `
                        linear-gradient(${primaryColor}10 1px, transparent 1px),
                        linear-gradient(90deg, ${primaryColor}10 1px, transparent 1px)
                    `,
                    backgroundSize: '50px 50px',
                    backgroundPosition: '25px -75px',
                    opacity: 0.4,
                    transform: 'rotate(1deg)',
                }} />

                {/* Radial vignette overlay for depth */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(ellipse at 50% 35%, transparent 0%, ${darkBg}70 65%, ${darkBg} 100%)`,
                }} />
            </Box>

            <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
                {/* Subtle glow overlay using primary color */}
                <Box sx={[styles.gradientOverlay, { 
                    background: `radial-gradient(circle at 50% 25%, ${primaryColor}12 0%, transparent 60%)`,
                }]} />
                
                {/* Floating Icon with Glow */}
                <Animated.View 
                    style={[
                        styles.iconWrapper,
                        { transform: [{ translateY: iconFloat }] }
                    ]}
                >
                    <Box
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            borderRadius: '50%',
                            padding: { xs: 2, sm: 3, md: 4 },
                            border: `2px solid ${primaryColor}40`,
                            boxShadow: `
                                0 0 60px ${primaryColor}50,
                                0 0 120px ${primaryColor}30,
                                inset 0 0 30px ${primaryColor}20
                            `,
                            marginBottom: 3,
                        }}
                    >
                        <Construction sx={{ 
                            fontSize: { xs: 25, sm: 30, md: 40 }, 
                            color: primaryColor,
                            filter: `drop-shadow(0 0 20px ${primaryColor}70)`
                        }} />
                    </Box>
                </Animated.View>
                
                {/* Store Name with Accent */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginBottom: 1,
                        padding: '8px 24px',
                        backgroundColor: `${primaryColor}20`,
                        borderRadius: '24px',
                        border: `1px solid ${primaryColor}40`,
                    }}
                >
                    <Typography 
                        variant='body1' 
                        sx={{ 
                            color: primaryColor,
                            fontWeight: 700,
                            letterSpacing: '1px',
                        }}
                    >
                        Currently Under Maintenance
                    </Typography>
                </Box>

                {/* Simple Message */}
                <Typography 
                    sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: '0.55rem', sm: '0.8rem', md: '1rem' },
                        lineHeight: 1.6,
                        textAlign: 'center',
                        maxWidth: 500,

                    }}
                >
                    We'll be back online shortly.
                </Typography>
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: '#0f172a', // Fallback color
    },
    content: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        paddingTop: 24,
        paddingBottom: 24,
        maxWidth: 700,
        width: '100%',
        zIndex: 2,
    },
    iconWrapper: {
        marginBottom: 8,
        zIndex: 2,
    },
    gradientOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
    },
});

